import { useState } from "react";
import { ChevronUp, Download, Loader2, X } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { Button, Infobulle } from "@/shared/ui";
import { useUpdater } from "./useUpdater";

/**
 * Toast bottom-right "Update vX.Y.Z disponible | Installer".
 *
 * Visible UNIQUEMENT dans Tauri + quand `useUpdater` retourne une update
 * dispo + tant que l'user ne l'a pas dismissed. Dismiss = session-only
 * (la toast reapparait au prochain launch tant que l'update reste pending).
 *
 * Mount au plus haut niveau du shell — App.tsx ou TitleBar — pour que la
 * toast soit visible peu importe le tab/panel courant.
 *
 * Dessin (coque, 01-11-2026) : la fenêtre en verre de la librairie
 * (`ov-fenetre`, celle de `Modal`) flottant en bas à droite — le lot « Toasts »
 * du catalogue n'est pas encore là — ; les boutons sont des `Button`, la
 * croix un `Button` fantôme ; plus de capitales espacées ni de contour.
 *
 * ⛔ Pas de bandeau en haut de la coque : essayé le 02-11, refusé le 03-11 par Kayla (« tout en haut,
 *    ça, c'est nul. Le truc d'avant, à droite, il était bien »). La fenêtre reste en bas à droite.
 */
export function UpdaterToast() {
  const { t } = useTranslation();
  const { available, busy, error } = useUpdater();
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!available || dismissed) return null;

  return (
    <div className="ov-fenetre ov-arrondi fixed bottom-4 right-4 z-[350] w-80 max-w-[calc(100vw-2rem)] p-4">
      <div className="flex items-start gap-3">
        <Download className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium leading-snug text-fg">
            {t("shell.updater.available", { version: available.version })}
          </p>
          {available.body && (
            <Button
              variant="ghost"
              size="md"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              icon={
                <ChevronUp
                  className={"h-3 w-3 transition-transform " + (expanded ? "" : "rotate-180")}
                />
              }
              className="-ml-2 mt-1"
            >
              Notes
            </Button>
          )}
          {expanded && available.body && (
            <p className="mt-1.5 max-h-32 overflow-y-auto whitespace-pre-wrap break-words text-[13px] leading-relaxed text-muted">
              {available.body}
            </p>
          )}
          {error && (
            <div className="mt-2 text-[13px] leading-snug" role="alert">
              <p className="font-medium text-erreur">{t("shell.updater.errorTitle")}</p>
              <p className="mt-0.5 text-muted">{t("shell.updater.errorBody")}</p>
            </div>
          )}
        </div>
        <Infobulle texte={t("shell.updater.dismiss")}>
          <Button
            variant="ghost"
            size="md"
            iconOnly
            onClick={() => setDismissed(true)}
            aria-label={t("shell.updater.dismiss")}
            className="-mr-1.5 -mt-1.5 shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </Infobulle>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button variant="secondary" size="lg" onClick={() => setDismissed(true)}>
          {t("shell.updater.dismiss")}
        </Button>
        <Button
          variant="accent"
          size="lg"
          onClick={() => void available.install()}
          disabled={busy}
          icon={
            busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )
          }
        >
          {busy ? t("shell.updater.installing") : t("shell.updater.install")}
        </Button>
      </div>
    </div>
  );
}
